import React from 'react';
import { Button } from '@roger/ui-components';
import { Loader2, CheckCircle } from 'lucide-react';
import type {
  KinnserCreatePatientResponse,
  RogerOnboardPatientResponse,
} from '../../services/api/types.gen';
import type { KinnserOnboardPatientResponse } from '../../hooks/useCreatePatientForm/useKinnserOnboarding';

interface PatientCreateButtonProps {
  handleCreate: () => Promise<void>;
  onComplete?: () => Promise<void>;
  creating: boolean;
  saveStatus: string;
  loadingClinics: boolean;
  loadingClinicData: boolean;
  selectedIntake: { status?: string } | null;
  isUsingOnboarding?: boolean;
  /** When false, button never shows success state (e.g. after "Mark Incomplete") */
  pushToEmrTabComplete?: boolean;
  createResult:
    | KinnserCreatePatientResponse
    | KinnserOnboardPatientResponse
    | RogerOnboardPatientResponse
    | null;
  isValid: boolean;
  pushToRoger?: boolean | null;
  pushToKinnser?: boolean | null;
  /** When true, button is disabled because intake is archived or non-admit */
  pushDisabledByIntakeState?: boolean;
  /**
   * Optional mode override. Defaults to "create" (standard onboarding).
   * "awaiting_scheduling": existing patient with no visit type picked yet —
   * never a push state, so the label points at scheduling rather than
   * claiming SOC/ROC or new-patient creation.
   * "rec": new-patient recert onboarding (Roger) — a create flow with its own
   * labels; uses the same handler/gating as "create".
   */
  mode?: 'create' | 'rec' | 'roc' | 'soc_existing' | 'awaiting_scheduling';
  /** When in ROC or soc_existing mode, indicates whether a patient has been explicitly selected from search results. */
  hasSelectedPatient?: boolean;
  /** When true, show "Onboard" label for Kinnser (e.g. new admission/episode auto_add flow). Does not change the handler. */
  preferOnboardLabel?: boolean;
  /** When true with pushToKinnser, success state shows "Patient Scheduled" instead of "Patient Onboarded". */
  isExistingPatient?: boolean;
  /**
   * Wellsky WRM clinics: Kinnser "schedule on existing episode" is a patient push from Roger,
   * not SOC/ROC scheduling copy.
   */
  useKinnserWrmPushCopy?: boolean;
  /** After a failed push/schedule attempt, keep the action disabled until the user gets support. */
  disableAfterFailure?: boolean;
  /** When true, parent wired a no-op handler (e.g. WRM blocks ROC/SOC scheduling). */
  pushActionDisabled?: boolean;
  className?: string;
}

export const PatientCreateButton: React.FC<PatientCreateButtonProps> = ({
  handleCreate,
  onComplete,
  creating,
  saveStatus,
  loadingClinics,
  loadingClinicData,
  selectedIntake,
  isUsingOnboarding,
  pushToEmrTabComplete = true,
  createResult,
  isValid,
  pushToRoger,
  pushToKinnser,
  pushDisabledByIntakeState = false,
  mode = 'create',
  hasSelectedPatient,
  preferOnboardLabel = false,
  isExistingPatient = false,
  useKinnserWrmPushCopy = false,
  disableAfterFailure = false,
  pushActionDisabled = false,
  className,
}) => {
  const isRocMode = mode === 'roc';
  const isRecMode = mode === 'rec';
  const isSocExistingMode = mode === 'soc_existing';
  const isAwaitingSchedulingMode = mode === 'awaiting_scheduling';
  // rec is intentionally NOT schedule-only: it is a create flow (new patient).
  const isScheduleOnlyMode =
    isRocMode || isSocExistingMode || isAwaitingSchedulingMode;
  const useWrmKinnserScheduleCopy = Boolean(
    useKinnserWrmPushCopy && pushToKinnser && isScheduleOnlyMode
  );
  const emrName = pushToRoger ? 'Roger' : pushToKinnser ? 'Kinnser' : 'NONE';
  const useOnboardLabel = isUsingOnboarding || preferOnboardLabel;
  const isRogerSuccess =
    pushToRoger &&
    (selectedIntake?.status === 'pushed_to_roger' ||
      (createResult as RogerOnboardPatientResponse)?.success === true);
  const isKinnserSuccess =
    pushToKinnser &&
    (selectedIntake?.status === 'pushed_to_kinnser' ||
      (isUsingOnboarding
        ? (createResult as KinnserOnboardPatientResponse | null)
            ?.patient_creation?.success === true
        : (createResult as KinnserCreatePatientResponse | null)?.success ===
          true));

  const isSocExistingSuccess =
    pushToKinnser &&
    isSocExistingMode &&
    (createResult as { success?: boolean } | null)?.success === true;
  const isSuccess =
    pushToEmrTabComplete &&
    (isRogerSuccess || isKinnserSuccess || isSocExistingSuccess);

  // Status stays PUSHED_TO_* even after "Mark Incomplete"; block re-onboarding to avoid duplicates
  const alreadyOnboardedOnce =
    selectedIntake?.status === 'pushed_to_kinnser' ||
    selectedIntake?.status === 'pushed_to_roger';

  // Button should be disabled if neither EMR is selected or intake is archived/non-admit
  const noEmrSelected = !pushToRoger && !pushToKinnser;

  const baseActionLabel = useWrmKinnserScheduleCopy
    ? 'Push to Kinnser'
    : isRecMode
      ? `Schedule REC in ${emrName}`
      : isSocExistingMode
        ? `Schedule SOC in ${emrName}`
        : isRocMode
          ? `Schedule ROC in ${emrName}`
          : isAwaitingSchedulingMode
            ? `Schedule Visit in ${emrName}`
            : useOnboardLabel
              ? `Onboard in ${emrName}`
              : `Create in ${emrName}`;

  return (
    <Button
      onClick={async () => {
        try {
          await handleCreate();
        } finally {
          // Best-effort refresh so status changes (e.g. push_failed) are reflected
          // even when the action errors.
          if (onComplete) {
            try {
              await onComplete();
            } catch {
              // ignore refresh errors; the original action error (if any) is more important
            }
          }
        }
      }}
      disabled={
        pushDisabledByIntakeState ||
        noEmrSelected ||
        pushActionDisabled ||
        creating ||
        saveStatus === 'saving' ||
        // A failed autosave leaves the server holding older field values than
        // the screen shows. Pushing then sends those older values to the EMR.
        // The status stays `error` until a save succeeds, so the block lifts as
        // soon as the form is saved again.
        saveStatus === 'error' ||
        loadingClinics ||
        loadingClinicData ||
        isSuccess ||
        alreadyOnboardedOnce ||
        !isValid ||
        (isScheduleOnlyMode && !hasSelectedPatient) ||
        disableAfterFailure
      }
      className={className}
    >
      {creating ? (
        <>
          <Loader2 className='h-4 w-4 animate-spin mr-2' />
          {useWrmKinnserScheduleCopy
            ? 'Pushing...'
            : isScheduleOnlyMode || isRecMode
              ? 'Scheduling...'
              : useOnboardLabel
                ? 'Onboarding...'
                : 'Creating...'}
        </>
      ) : isSuccess || alreadyOnboardedOnce ? (
        <>
          <CheckCircle className='h-4 w-4 mr-2' />
          {alreadyOnboardedOnce && !isSuccess
            ? 'Patient already onboarded'
            : useKinnserWrmPushCopy &&
                pushToKinnser &&
                (isKinnserSuccess || isSocExistingSuccess)
              ? 'Pushed to Kinnser'
              : isSocExistingSuccess
                ? `SOC Scheduled in ${emrName}`
                : isRecMode
                  ? `REC Scheduled in ${emrName}`
                  : isRocMode
                    ? `ROC Scheduled in ${emrName}`
                    : useOnboardLabel && pushToKinnser && isExistingPatient
                      ? `Patient Scheduled in ${emrName}`
                      : useOnboardLabel
                        ? `Patient Onboarded in ${emrName}`
                        : `Patient Created in ${emrName}`}
        </>
      ) : (
        <>
          <CheckCircle className='h-4 w-4 mr-2' />
          {disableAfterFailure ? 'Push failed' : baseActionLabel}
        </>
      )}
    </Button>
  );
};
